import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DoctorLayout from "../../Layout/DoctorLayout";
import API from "../../Api/Api";
import ENDPOINTS from "../../Api/Endpoints";
import { tokenManager } from "../../Api/tokenManager";
import { showAlert } from "../../feedback/feedbackApi";
import {
  DAYS,
  TIME_OPTIONS_30_MIN,
  formatDisplayTime,
  isValidHospitalScheduleRow,
  newHospitalScheduleRow,
  normalizeTo30Minute,
  schedulesToHospitalRows,
} from "../../utils/providerSlotUtils";
import ProviderSpecialtiesEditor from "./ProviderSpecialtiesEditor";
import "./DoctorSettings.scss";

const SLOT_DURATIONS = [15, 30, 45, 60];
const normalizeMe = (raw) => raw?.data?.data || raw?.data || raw?.doctor || raw;

function getInitials(name) {
  const s = String(name || "").trim();
  if (!s) return "?";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase().slice(0, 2);
  }
  return s.slice(0, 2).toUpperCase();
}

/** When `image` is empty, use first clinic Place photo (same idea as patient chat). */
function pickClinicPlacePhotoUrl(profile) {
  if (!profile || typeof profile !== "object") return "";
  const clinics = Array.isArray(profile.clinicSchedules) ? profile.clinicSchedules : [];
  for (const c of clinics) {
    const single = String(c?.placePhotoUrl || "").trim();
    if (single) return single;
    const urls = c?.placePhotoUrls;
    if (Array.isArray(urls)) {
      const first = urls.map((x) => String(x || "").trim()).find(Boolean);
      if (first) return first;
    }
  }
  return "";
}

const firstFilled = (...vals) => {
  for (const v of vals) {
    const text = String(v ?? "").trim();
    if (text) return text;
  }
  return "—";
};

const normalizeField = (value) => String(value ?? "").trim();
const normalizePhone = (value) => String(value ?? "").replace(/\D/g, "").slice(0, 10);
const normalizePincode = (value) => String(value ?? "").replace(/\D/g, "").slice(0, 6);
const cleanEditablePayload = (draft, isHospital) => {
  const payload = {
    email: normalizeField(draft.email).toLowerCase(),
    mobile: normalizePhone(draft.mobile),
    state: normalizeField(draft.state),
    pincode: normalizePincode(draft.pincode),
    hospitalClinicName: normalizeField(draft.hospitalClinicName),
  };
  if (isHospital) {
    payload.city = normalizeField(draft.cityOfPractice);
  } else {
    payload.cityOfPractice = normalizeField(draft.cityOfPractice);
    payload.yearsOfExperience = draft.yearsOfExperience === "" ? undefined : Number(draft.yearsOfExperience);
  }
  return payload;
};

export default function DoctorSettings() {
  const navigate = useNavigate();
  const isHospital = tokenManager.getActiveProviderRole() === "hospital";
  const mePath = isHospital ? ENDPOINTS.HOSPITAL_ME : ENDPOINTS.DOCTOR_ME;
  const savePath = ENDPOINTS.HOSPITAL_UPDATE_AVAILABILITY;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);

  const [emergency24x7, setEmergency24x7] = useState(false);
  const [hospitalRows, setHospitalRows] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [submittingEdit, setSubmittingEdit] = useState(false);
  const [editDraft, setEditDraft] = useState({
    email: "",
    mobile: "",
    cityOfPractice: "",
    state: "",
    pincode: "",
    hospitalClinicName: "",
    yearsOfExperience: "",
  });
  const [otpStage, setOtpStage] = useState({ open: false, value: "", contactChanges: false });
  const [profileImageFailed, setProfileImageFailed] = useState(false);
  const [degreeDraft, setDegreeDraft] = useState([{ id: "deg-1", title: "", institution: "", year: "" }]);
  const [editingDegrees, setEditingDegrees] = useState(false);
  const [submittingDegrees, setSubmittingDegrees] = useState(false);

  const snapshot = useMemo(() => {
    const p = profile || {};
    return {
      name: firstFilled(p.name),
      email: firstFilled(p.email),
      phone: firstFilled(p.mobile, p.phone, p.phoneNumber),
      city: firstFilled(p.cityOfPractice, p.city),
      state: firstFilled(p.state),
      pincode: firstFilled(p.pincode),
      facility: firstFilled(p.hospitalClinicName, p.clinicName, p.hospitalName),
      specialization: firstFilled(
        p.specialization,
        p.speciality,
        Array.isArray(p.specializations) ? p.specializations.join(", ") : "",
        Array.isArray(p.specialties) ? p.specialties.join(", ") : ""
      ),
      registrationId: firstFilled(
        p.regId,
        p.registrationId,
        p.registrationNumber,
        p.registrationNo,
        p.regNo,
        p.licenseNumber,
        p.licenseNo,
        p.medicalRegistrationNumber
      ),
      experience: firstFilled(
        p.yearsOfExperience,
        p.experienceYears,
        p.experience,
        p.totalExperience
      ),
      degrees: Array.isArray(p.degrees) && p.degrees.length
        ? p.degrees
        : String(p.degree || "")
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean)
            .map((title) => ({ title, institution: "", year: null })),
      pendingDegreesEdit: p.pendingDegreesEdit && Array.isArray(p.pendingDegreesEdit.degrees)
        ? p.pendingDegreesEdit
        : null,
      hospitalType: isHospital ? String(p.hospitalType || "").trim() || "—" : null,
    };
  }, [profile, isHospital]);

  const syncDegreeDraftFromProfile = useCallback((p) => {
    const live =
      Array.isArray(p?.degrees) && p.degrees.length
        ? p.degrees
        : String(p?.degree || "")
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean)
            .map((title) => ({ title, institution: "", year: "" }));
    const rows = (live.length ? live : [{ title: "", institution: "", year: "" }]).map((d, i) => ({
      id: `deg-${i}-${String(d.title || "x")}`,
      title: String(d.title || ""),
      institution: String(d.institution || ""),
      year: d.year == null || d.year === "" ? "" : String(d.year),
    }));
    setDegreeDraft(rows);
  }, []);

  const profilePhotoUrl = useMemo(() => {
    const p = profile || {};
    if (isHospital) {
      return String(p.avatar || p.image || "").trim();
    }
    return String(p.image || "").trim() || pickClinicPlacePhotoUrl(p);
  }, [profile, isHospital]);

  useEffect(() => {
    setProfileImageFailed(false);
  }, [profilePhotoUrl]);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get(mePath);
      const p = normalizeMe(res?.data);
      setProfile(p || {});
      syncDegreeDraftFromProfile(p || {});
      if (isHospital) {
        setEmergency24x7(!!p?.emergency24x7);
        const rows = schedulesToHospitalRows(p?.departmentOpdSchedules);
        setHospitalRows(rows.length ? rows : [newHospitalScheduleRow()]);
      }
    } catch (err) {
      showAlert(err?.response?.data?.message || err?.message || "Could not load profile", {
        variant: "error",
        title: "Settings",
      });
    } finally {
      setLoading(false);
    }
  }, [isHospital, mePath, syncDegreeDraftFromProfile]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    const p = profile || {};
    setEditDraft({
      email: normalizeField(p.email),
      mobile: normalizePhone(p.mobile || p.phone || p.phoneNumber),
      cityOfPractice: normalizeField(p.cityOfPractice || p.city),
      state: normalizeField(p.state),
      pincode: normalizePincode(p.pincode),
      hospitalClinicName: normalizeField(p.hospitalClinicName || p.clinicName || p.hospitalName),
      yearsOfExperience: normalizeField(p.yearsOfExperience ?? p.experienceYears ?? p.experience),
    });
  }, [profile]);

  const updateHospitalRow = (id, patch) => {
    setHospitalRows((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const updateEditDraft = (key, value) => {
    setEditDraft((prev) => ({ ...prev, [key]: value }));
  };

  const cancelEdit = () => {
    const p = profile || {};
    setEditDraft({
      email: normalizeField(p.email),
      mobile: normalizePhone(p.mobile || p.phone || p.phoneNumber),
      cityOfPractice: normalizeField(p.cityOfPractice || p.city),
      state: normalizeField(p.state),
      pincode: normalizePincode(p.pincode),
      hospitalClinicName: normalizeField(p.hospitalClinicName || p.clinicName || p.hospitalName),
      yearsOfExperience: normalizeField(p.yearsOfExperience ?? p.experienceYears ?? p.experience),
    });
    setOtpStage({ open: false, value: "", contactChanges: false });
    setEditMode(false);
  };

  const requestProfileEditOtp = async () => {
    const payload = cleanEditablePayload(editDraft, isHospital);
    const currentPayload = cleanEditablePayload(
      {
        email: profile?.email,
        mobile: profile?.mobile || profile?.phone || profile?.phoneNumber,
        cityOfPractice: profile?.cityOfPractice || profile?.city,
        state: profile?.state,
        pincode: profile?.pincode,
        hospitalClinicName: profile?.hospitalClinicName || profile?.clinicName || profile?.hospitalName,
        yearsOfExperience: profile?.yearsOfExperience ?? profile?.experienceYears ?? profile?.experience,
      },
      isHospital
    );
    const changed = {};
    Object.keys(payload).forEach((k) => {
      if (payload[k] !== currentPayload[k]) changed[k] = payload[k];
    });
    if (Object.keys(changed).length === 0) {
      showAlert("No changes detected.", { variant: "warning" });
      return;
    }

    const requestEditPath = isHospital ? ENDPOINTS.HOSPITAL_REQUEST_PROFILE_EDIT : ENDPOINTS.DOCTOR_REQUEST_PROFILE_EDIT;
    setSubmittingEdit(true);
    try {
      await API.patch(requestEditPath, changed);
      const contactChanges = Object.prototype.hasOwnProperty.call(changed, "email") || Object.prototype.hasOwnProperty.call(changed, "mobile");
      setOtpStage({ open: true, value: "", contactChanges });
      showAlert("OTP sent to your registered mobile number.", { variant: "success" });
    } catch (err) {
      showAlert(err?.response?.data?.message || err?.message || "Could not send OTP", {
        variant: "error",
        title: "Settings",
      });
    } finally {
      setSubmittingEdit(false);
    }
  };

  const verifyProfileEditOtp = async () => {
    const otp = String(otpStage.value || "").replace(/\D/g, "").slice(0, 4);
    if (!otp || otp.length < 4) {
      showAlert("Enter the 4-digit OTP.", { variant: "warning" });
      return;
    }
    const verifyEditPath = isHospital ? ENDPOINTS.HOSPITAL_VERIFY_PROFILE_EDIT : ENDPOINTS.DOCTOR_VERIFY_PROFILE_EDIT;
    setSubmittingEdit(true);
    try {
      await API.patch(verifyEditPath, { otp });
      showAlert("Profile changes saved.", { variant: "success" });
      setEditMode(false);
      setOtpStage({ open: false, value: "", contactChanges: false });
      await loadProfile();
    } catch (err) {
      showAlert(err?.response?.data?.message || err?.message || "OTP verification failed", {
        variant: "error",
        title: "Settings",
      });
    } finally {
      setSubmittingEdit(false);
    }
  };

  const submitDegreesForApproval = async () => {
    const degrees = degreeDraft
      .map((d) => ({
        title: String(d.title || "").trim(),
        institution: String(d.institution || "").trim(),
        year: String(d.year || "").trim() || undefined,
      }))
      .filter((d) => d.title);
    if (degrees.length === 0) {
      showAlert("Add at least one medical degree.", { variant: "warning" });
      return;
    }
    setSubmittingDegrees(true);
    try {
      await API.patch(ENDPOINTS.DOCTOR_REQUEST_DEGREES_EDIT, { degrees });
      showAlert("Degree changes submitted for admin approval.", { variant: "success" });
      setEditingDegrees(false);
      await loadProfile();
    } catch (err) {
      showAlert(err?.response?.data?.message || err?.message || "Could not submit degree changes", {
        variant: "error",
        title: "Degrees",
      });
    } finally {
      setSubmittingDegrees(false);
    }
  };

  const cancelPendingDegrees = async () => {
    setSubmittingDegrees(true);
    try {
      await API.patch(ENDPOINTS.DOCTOR_CANCEL_DEGREES_EDIT, {});
      showAlert("Pending degree request cancelled.", { variant: "success" });
      await loadProfile();
    } catch (err) {
      showAlert(err?.response?.data?.message || err?.message || "Could not cancel request", {
        variant: "error",
        title: "Degrees",
      });
    } finally {
      setSubmittingDegrees(false);
    }
  };

  const saveHospital = async () => {
    const validHospital = hospitalRows.filter(isValidHospitalScheduleRow);
    if (validHospital.length === 0) {
      showAlert("Add at least one department row with a name, day, valid times, and slot length (5-120 minutes).", {
        variant: "warning",
      });
      return;
    }

    const departmentOpdSchedules = validHospital.map((r) => ({
      departmentName: r.departmentName.trim(),
      weekdays: [r.day],
      startTime: normalizeTo30Minute(r.startTime),
      endTime: normalizeTo30Minute(r.endTime),
      slotDurationMinutes: Number(r.slotDurationMinutes) || 30,
      notes: String(r.notes || "").trim().slice(0, 300),
    }));

    setSaving(true);
    try {
      await API.patch(savePath, { emergency24x7, departmentOpdSchedules });
      showAlert("OPD schedules saved.", { variant: "success" });
      await loadProfile();
    } catch (err) {
      showAlert(err?.response?.data?.message || err?.message || "Save failed", { variant: "error", title: "Settings" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DoctorLayout>
      <div className="doc_settings_page">
        {loading ? (
          <div className="doc_settings_card doc_settings_muted">Loading...</div>
        ) : (
          <>
            <section className="doc_settings_card">
              <div className="doc_settings_title_row">
                <h2 className="doc_settings_section_title">Account</h2>
                <button type="button" className="doc_settings_secondary doc_settings_secondary--small" onClick={() => setEditMode((v) => !v)}>
                  {editMode ? "Stop editing" : "Edit details"}
                </button>
              </div>
              <div className="doc_settings_account_banner">
                <div
                  className={`doc_settings_avatar${profilePhotoUrl && !profileImageFailed ? " doc_settings_avatar--photo" : ""}`}
                  role="img"
                  aria-label={
                    profilePhotoUrl && !profileImageFailed ? "Profile photo" : `Initials for ${snapshot.name}`
                  }
                >
                  {profilePhotoUrl && !profileImageFailed ? (
                    <img src={profilePhotoUrl} alt="" onError={() => setProfileImageFailed(true)} />
                  ) : (
                    <span className="doc_settings_avatar_initials">{getInitials(snapshot.name)}</span>
                  )}
                </div>
                <p className="doc_settings_avatar_caption">
                  {isHospital
                    ? "This photo is shown to patients when your facility appears in search, chat, and lists."
                    : "This photo is shown to patients in chat and lists. It comes from your registration portrait, or from your first clinic’s Google listing photo if no portrait is stored."}
                </p>
              </div>
              <div className="doc_settings_grid">
                <div>
                  <div className="doc_settings_k">Name</div>
                  <div className="doc_settings_v">{snapshot.name}</div>
                </div>
                <div>
                  <div className="doc_settings_k">Email</div>
                  {editMode ? (
                    <input className="doc_settings_input" value={editDraft.email} onChange={(e) => updateEditDraft("email", e.target.value)} placeholder="Email" />
                  ) : (
                    <div className="doc_settings_v">{snapshot.email}</div>
                  )}
                </div>
                <div>
                  <div className="doc_settings_k">Phone</div>
                  {editMode ? (
                    <input className="doc_settings_input" value={editDraft.mobile} onChange={(e) => updateEditDraft("mobile", normalizePhone(e.target.value))} placeholder="Mobile number" />
                  ) : (
                    <div className="doc_settings_v">{snapshot.phone}</div>
                  )}
                </div>
                <div>
                  <div className="doc_settings_k">Location</div>
                  {editMode ? (
                    <div className="doc_settings_inline_3">
                      <input className="doc_settings_input" value={editDraft.cityOfPractice} onChange={(e) => updateEditDraft("cityOfPractice", e.target.value)} placeholder="City" />
                      <input className="doc_settings_input" value={editDraft.state} onChange={(e) => updateEditDraft("state", e.target.value)} placeholder="State" />
                      <input className="doc_settings_input" value={editDraft.pincode} onChange={(e) => updateEditDraft("pincode", normalizePincode(e.target.value))} placeholder="Pincode" />
                    </div>
                  ) : (
                    <div className="doc_settings_v">{snapshot.city}, {snapshot.state} {snapshot.pincode}</div>
                  )}
                </div>
                <div>
                  <div className="doc_settings_k">{isHospital ? "Facility" : "Clinic / hospital"}</div>
                  {editMode ? (
                    <input className="doc_settings_input" value={editDraft.hospitalClinicName} onChange={(e) => updateEditDraft("hospitalClinicName", e.target.value)} placeholder="Clinic / hospital" />
                  ) : (
                    <div className="doc_settings_v">{snapshot.facility}</div>
                  )}
                </div>
                {snapshot.hospitalType != null ? (
                  <div>
                    <div className="doc_settings_k">Hospital type</div>
                    <div className="doc_settings_v">{snapshot.hospitalType}</div>
                  </div>
                ) : null}
              </div>
              {editMode ? (
                <>
                  <div className="doc_settings_actions">
                    <button type="button" className="doc_settings_save" onClick={requestProfileEditOtp} disabled={submittingEdit}>
                      {submittingEdit ? "Sending OTP..." : "Send OTP to confirm updates"}
                    </button>
                    <button type="button" className="doc_settings_secondary" onClick={cancelEdit} disabled={submittingEdit}>
                      Cancel
                    </button>
                  </div>
                  {otpStage.open ? (
                    <div className="doc_settings_otp_box">
                      <div className="doc_settings_k">Verify OTP</div>
                      <p className="doc_settings_hint">
                        {otpStage.contactChanges
                          ? "Email/mobile changes are protected. Enter OTP sent to your current registered mobile."
                          : "Enter OTP sent to your registered mobile to save profile updates."}
                      </p>
                      <div className="doc_settings_otp_row">
                        <input className="doc_settings_input" value={otpStage.value} onChange={(e) => setOtpStage((s) => ({ ...s, value: e.target.value.replace(/\D/g, "").slice(0, 4) }))} placeholder="4-digit OTP" />
                        <button type="button" className="doc_settings_save" onClick={verifyProfileEditOtp} disabled={submittingEdit}>
                          {submittingEdit ? "Verifying..." : "Verify & save"}
                        </button>
                      </div>
                    </div>
                  ) : null}
                </>
              ) : (
                <p className="doc_settings_hint">Editable here: email, mobile, location, clinic/hospital, and experience. Name/documents stay admin-controlled.</p>
              )}
            </section>

            {!isHospital && (
              <section className="doc_settings_card">
                <h2 className="doc_settings_section_title">Professional details</h2>
                <div className="doc_settings_grid doc_settings_grid--doctor">
                  <div>
                    <div className="doc_settings_k">Experience</div>
                    {editMode ? (
                      <input className="doc_settings_input" value={editDraft.yearsOfExperience} onChange={(e) => updateEditDraft("yearsOfExperience", e.target.value.replace(/[^\d]/g, "").slice(0, 2))} placeholder="Years" />
                    ) : (
                      <div className="doc_settings_v">{snapshot.experience === "—" ? "—" : `${snapshot.experience} years`}</div>
                    )}
                  </div>
                  <div>
                    <div className="doc_settings_k">Registration / license</div>
                    <div className="doc_settings_v">{snapshot.registrationId}</div>
                  </div>
                </div>

                <div className="doc_settings_title_row" style={{ marginTop: 16 }}>
                  <h3 className="doc_settings_section_title" style={{ fontSize: 16, margin: 0 }}>
                    Medical degrees
                  </h3>
                  {!snapshot.pendingDegreesEdit ? (
                    <button
                      type="button"
                      className="doc_settings_secondary doc_settings_secondary--small"
                      onClick={() => {
                        if (editingDegrees) {
                          syncDegreeDraftFromProfile(profile || {});
                          setEditingDegrees(false);
                        } else {
                          setEditingDegrees(true);
                        }
                      }}
                    >
                      {editingDegrees ? "Cancel" : "Edit degrees"}
                    </button>
                  ) : null}
                </div>

                {snapshot.pendingDegreesEdit ? (
                  <div style={{ marginTop: 10 }}>
                    <p className="doc_settings_hint">
                      Degree changes are awaiting HealBid admin approval.
                      {snapshot.pendingDegreesEdit.note ? ` Note: ${snapshot.pendingDegreesEdit.note}` : ""}
                    </p>
                    <ul style={{ margin: "8px 0 12px", paddingLeft: 18 }}>
                      {(snapshot.pendingDegreesEdit.degrees || []).map((d, i) => (
                        <li key={`pending-deg-${i}`}>
                          <strong>{d.title}</strong>
                          {d.institution ? ` · ${d.institution}` : ""}
                          {d.year ? ` · ${d.year}` : ""}
                        </li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      className="doc_settings_secondary"
                      onClick={cancelPendingDegrees}
                      disabled={submittingDegrees}
                    >
                      {submittingDegrees ? "Cancelling..." : "Withdraw request"}
                    </button>
                  </div>
                ) : editingDegrees ? (
                  <div style={{ marginTop: 10 }}>
                    {(degreeDraft || []).map((row) => (
                      <div key={row.id} className="doc_settings_slot_row" style={{ marginBottom: 8 }}>
                        <input
                          className="doc_settings_input"
                          value={row.title}
                          onChange={(e) =>
                            setDegreeDraft((rows) =>
                              rows.map((r) => (r.id === row.id ? { ...r, title: e.target.value } : r))
                            )
                          }
                          placeholder="Degree (e.g. MBBS)"
                        />
                        <input
                          className="doc_settings_input"
                          value={row.institution}
                          onChange={(e) =>
                            setDegreeDraft((rows) =>
                              rows.map((r) => (r.id === row.id ? { ...r, institution: e.target.value } : r))
                            )
                          }
                          placeholder="Institution (optional)"
                        />
                        <input
                          className="doc_settings_input"
                          value={row.year}
                          onChange={(e) =>
                            setDegreeDraft((rows) =>
                              rows.map((r) =>
                                r.id === row.id
                                  ? { ...r, year: e.target.value.replace(/\D/g, "").slice(0, 4) }
                                  : r
                              )
                            )
                          }
                          placeholder="Year"
                        />
                        <button
                          type="button"
                          className="doc_settings_secondary doc_settings_secondary--small"
                          onClick={() =>
                            setDegreeDraft((rows) => (rows.length <= 1 ? rows : rows.filter((r) => r.id !== row.id)))
                          }
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <div className="doc_settings_action_row">
                      <button
                        type="button"
                        className="doc_settings_secondary"
                        onClick={() =>
                          setDegreeDraft((rows) =>
                            rows.length >= 10
                              ? rows
                              : [
                                  ...rows,
                                  {
                                    id: `deg-${Date.now()}`,
                                    title: "",
                                    institution: "",
                                    year: "",
                                  },
                                ]
                          )
                        }
                      >
                        Add degree
                      </button>
                      <button
                        type="button"
                        className="doc_settings_save"
                        onClick={submitDegreesForApproval}
                        disabled={submittingDegrees}
                      >
                        {submittingDegrees ? "Submitting..." : "Submit for admin approval"}
                      </button>
                    </div>
                    <p className="doc_settings_hint">
                      Degree edits require HealBid admin approval before they appear on your public profile.
                    </p>
                  </div>
                ) : (
                  <div style={{ marginTop: 10 }}>
                    {(snapshot.degrees || []).length === 0 ? (
                      <div className="doc_settings_v">No degrees on file yet.</div>
                    ) : (
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {snapshot.degrees.map((d, i) => (
                          <li key={`live-deg-${i}`}>
                            <strong>{d.title}</strong>
                            {d.institution ? ` · ${d.institution}` : ""}
                            {d.year ? ` · ${d.year}` : ""}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                <p className="doc_settings_hint">Categories and subcategories are managed in the unified checklist below. Your previously selected items are pre-checked there.</p>
              </section>
            )}

            {!isHospital && (
              <section className="doc_settings_card doc_settings_mobile_slots" aria-label="Mobile slot actions">
                <h2 className="doc_settings_section_title">Slots</h2>
                <p className="doc_settings_sub">
                  Manage your availability slots from here on mobile.
                </p>
                <div className="doc_settings_action_row">
                  <button
                    type="button"
                    className="doc_settings_save"
                    onClick={() => navigate("/doctor-availability")}
                  >
                    Add slots
                  </button>
                  <button
                    type="button"
                    className="doc_settings_secondary"
                    onClick={() => navigate("/doctor-availability")}
                  >
                    Edit slots
                  </button>
                </div>
              </section>
            )}

            <ProviderSpecialtiesEditor isHospital={isHospital} />

            {isHospital && (
              <>
                <section className="doc_settings_card">
                  <h2 className="doc_settings_section_title">OPD and availability</h2>
                  <label className="doc_settings_check">
                    <input type="checkbox" checked={emergency24x7} onChange={(e) => setEmergency24x7(e.target.checked)} />
                    <span>Emergency services 24x7</span>
                  </label>
                  <p className="doc_settings_sub">Each row is one department block for a single weekday.</p>
                  <div className="doc_settings_slot_list">
                    {hospitalRows.map((row) => (
                      <div key={row.id} className="doc_settings_slot_row">
                        <input className="doc_settings_input doc_settings_input--dept" value={row.departmentName} onChange={(e) => updateHospitalRow(row.id, { departmentName: e.target.value })} placeholder="Department" aria-label="Department name" />
                        <select className="doc_settings_input" value={row.day} onChange={(e) => updateHospitalRow(row.id, { day: e.target.value })} aria-label="Day">
                          {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
                        </select>
                        <select className="doc_settings_input" value={normalizeTo30Minute(row.startTime)} onChange={(e) => updateHospitalRow(row.id, { startTime: e.target.value })} aria-label="Start time">
                          {TIME_OPTIONS_30_MIN.map((t) => <option key={t} value={t}>{formatDisplayTime(t)}</option>)}
                        </select>
                        <select className="doc_settings_input" value={normalizeTo30Minute(row.endTime)} onChange={(e) => updateHospitalRow(row.id, { endTime: e.target.value })} aria-label="End time">
                          {TIME_OPTIONS_30_MIN.map((t) => <option key={t} value={t}>{formatDisplayTime(t)}</option>)}
                        </select>
                        <select className="doc_settings_input doc_settings_input--narrow" value={row.slotDurationMinutes} onChange={(e) => updateHospitalRow(row.id, { slotDurationMinutes: Number(e.target.value) })} aria-label="Slot length">
                          {SLOT_DURATIONS.map((m) => <option key={m} value={m}>{m} min</option>)}
                        </select>
                        <input className="doc_settings_input doc_settings_input--grow" value={row.notes} onChange={(e) => updateHospitalRow(row.id, { notes: e.target.value })} placeholder="Notes (optional)" />
                        <button type="button" className="doc_settings_icon_btn" onClick={() => setHospitalRows((r) => r.filter((x) => x.id !== row.id))} disabled={hospitalRows.length <= 1} aria-label="Remove row">x</button>
                      </div>
                    ))}
                  </div>
                  <button type="button" className="doc_settings_link_btn" onClick={() => setHospitalRows((r) => [...r, newHospitalScheduleRow()])}>
                    + Add department row
                  </button>
                </section>

                <div className="doc_settings_actions">
                  <button type="button" className="doc_settings_save" onClick={saveHospital} disabled={saving || loading}>
                    {saving ? "Saving..." : "Save changes"}
                  </button>
                  <button type="button" className="doc_settings_secondary" onClick={loadProfile} disabled={saving || loading}>
                    Reload
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </DoctorLayout>
  );
}
